import Axios from "axios";
import { config } from "./config";
import GoogleAuth from "./framework/GoogleAuth";
import { appVersion } from "./framework/utils/app-version";
import { generateDeviceFingerprint } from "./framework/utils/device-fingerprint";
import store from './store'

const api = Axios.create({ baseURL: `${config.romeApi}`, headers: {
    'Content-Type': 'application/x-www-form-urlencoded'
}});

const clientId = 'AmeriflexApp';
var useRefreshToken = false;

async function getConfig() {
    useRefreshToken = await appVersion.useRefreshToken();
}

async function callAuth(p) {
    var response = new AuthResponse();
    try {
        await getConfig();
        let result = await api.post('authorize', p);
        if(result && result.data && result.data.access_token) {
            response.access_token = result.data.access_token;
            response.refresh_token = result.data.refresh_token;
        }
    }
    catch(e) {
        if(e.response.data.error == 'redirect') {
            window.location.href = e.response.data.error_description;
            return;
        }

        if(e.response.data.error == 'mfa_required') {
            response.error = 'mfa_required';
            response.data = JSON.parse(e.response.data.error_description);
            return response;
        }

        response.error = e.response && e.response.data ? e.response.data.error_description : "";
    }

    return response;
}

class AuthResponse {
    access_token = null;
    refresh_token = null;
    error = null;
    userInfo = null;
    data = null;

    get authenticated() {
        return !!(this.access_token && this.refresh_token);
    }
}

function getCredentialPayload(data) {
    let url = `username=${data.username}&password=${data.password}&grant_type=password&client_id=${clientId}&loginProperties=${btoa(JSON.stringify(data.loginProperties))}`;
    return url;
}

class RomeAuth {

    async verifyMfa(username, password, otp) {
        username = encodeURIComponent(username);
        password = encodeURIComponent(password);
        var fingerprint = await generateDeviceFingerprint();

        let url = getCredentialPayload({
            username,
            password,
            loginProperties: {
                fingerprint,
                otp
            }
        })

        let response = await callAuth(url);
        await introspect(response);

        return response;
    }

    async mfaComplete(response) {
        await storeLoginInfo(response);
        return response;
    }

    async credentials(username, password) {
        username = encodeURIComponent(username);
        password = encodeURIComponent(password);
        var fingerprint = await generateDeviceFingerprint();
        
        let url = getCredentialPayload({
            username,
            password,
            loginProperties: {
                fingerprint
            }
        });

        let response = await callAuth(url);

        await introspect(response);
        await storeLoginInfo(response);
        return response;
    }

    async token(jwt) {
        let response = await callAuth(`token=${jwt}&grant_type=jwt&client_id=${clientId}`);
        await introspect(response);
        await storeLoginInfo(response);
        return response;
    }

    async continueSession() {
        await getConfig();
        await this.refresh();
        timer.newSession();
    }

    async refresh() {
        if(!useRefreshToken) return;
        var refresh_token = localStorage.getItem('refresh_token');
        if(!refresh_token) return null;
        let response = await callAuth(`refresh_token=${refresh_token}&grant_type=refresh_token&clientId=${clientId}`);

        if(!response.authenticated) {
            await this.logoutWithMessageSessionExpired();
            return response;
        }

        await storeLoginInfo(response, false);
        return response;
    }

    async google(jwt, username, externalSite = false) {
        username = encodeURIComponent(username);
        let response = await callAuth(`google_jwt=${jwt}&username=${username}&grant_type=gsuite&client_id=${clientId}`);
        if(!externalSite) {
            await introspect(response);
            await storeLoginInfo(response);
        }
        return response;
    }

    async logout() {
        timer.clearTimers();
        store.commit('session/clear');
        localStorage.removeItem('refresh_token');
    }

    async logoutWithMessageSessionExpired() {
        await store.dispatch('session/setError', 'You have been logged out after a period of inactivity, please log in.');
        this.logout();
        setTimeout(() => { window.location.href = `${config.romeUi}/#/login`; });
    }
}

async function storeLoginInfo(response, isNewSession = true) {
    if(!response.authenticated) return;
    var userInfo = response.userInfo;
    if(!userInfo) userInfo = localStorage.getItem('userInfo') ? JSON.parse(localStorage.getItem('userInfo')) : null;
    localStorage.setItem('refresh_token', response.refresh_token);
    await store.dispatch('session/login', { apiToken: response.access_token, userInfo });
    if(isNewSession) timer.newSession();
}

async function introspect(response) {
    let jwt = response.access_token;
    if(!jwt) return;
    let i = Axios.create({
        baseURL: config.romeApi,
        headers: {
            Authorization: `Bearer ${jwt}`,
            'Content-Type': 'application/x-www-form-urlencoded'
        }
    });

    let info = await i.post(`token/introspect`, `token=${jwt}`);
    if(info.data && info.data.active)
    {
        var data = info.data;
        var userInfo = {
            firstName: data.claims.firstName,
            lastName: data.claims.lastName,
            email: data.email,
            userId: data.userId,
            get fullName() {
                return `${this.firstName} ${this.lastName}`;
            },
            userRoleName: data.roles[0],
            tokenInfo: data,
            gsuite_user: data.gsuite_user,
            isInternalUser: data ? data.isInternalUser : false,
            isFrontLinePX: data ? data.isFrontLinePX : false,
            mfaMethod: data.mfaMethod
        }

        GoogleAuth.setGsuiteUser(userInfo.gsuite_user === true);
        response.userInfo = userInfo;
    }
}

const auth = new RomeAuth();

class AuthTimer {
    timer = null;
    oneMinute = 60000;
    //oneMinute = 1000;

    activity() {
        this.resetItem('lastActive');
    }

    minutesDiff(item) {
        var now = Date.now();
        var when = localStorage.getItem(item) || 0;
        if(when == 0) return 100;
        when = parseInt(when);
        var diff = now - when;
        var min = Math.round(diff / this.oneMinute);
        return min;

    }

    resetItem(item) {
        localStorage.setItem(item, Date.now());
    }

    clearTimers() {
        if(this.timer) clearInterval(this.timer);
        localStorage.removeItem('lastActive');
        localStorage.removeItem('lastToken');
    }

    logoutIfIdle() {
        if(this.minutesDiff('lastActive') > 30) {
            auth.logoutWithMessageSessionExpired();
        }
    }

    requestNewTokenAfter20Minutes() {
        if(this.minutesDiff('lastToken') >= 20) {
            auth.refresh();
            this.resetItem('lastToken');
        }
    }

    newSession() {
        this.clearTimers();
        if(!useRefreshToken) return;
        this.timer = setInterval(() => {
            this.requestNewTokenAfter20Minutes();
            this.logoutIfIdle();
        }, this.oneMinute);
        this.resetItem('lastToken');
        this.resetItem('lastActive');
    }
}

document.onclick = function() {
    timer.activity();
}

const timer = new AuthTimer();
export default auth;



// WEBPACK FOOTER //
// ./src/auth.js